/**
 * V54: 템플릿 이름 입력 모달
 *
 * 새 템플릿 첫 저장 시 이름 입력을 강제하기 위한 모달입니다.
 *
 * @module pages/organization/components/seatMap/TemplateNameModal
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Loader } from 'lucide-react';
import { useModalOverlayClose } from '../../../../shared/hooks/useModalOverlayClose';

// ============================================
// Props 타입
// ============================================

export interface TemplateNameModalProps {
  /** 모달 열림 상태 */
  isOpen: boolean;
  /** 모달 닫기 핸들러 */
  onClose: () => void;
  /** 저장 핸들러 (이름 반환) */
  onSave: (name: string) => void;
  /** 초기 이름 (수정 모드) */
  initialName?: string;
  /** 저장 중 상태 */
  isSaving?: boolean;
}

// ============================================
// 모달 컴포넌트
// ============================================

export function TemplateNameModal({
  isOpen,
  onClose,
  onSave,
  initialName = '',
  isSaving = false,
}: TemplateNameModalProps) {
  const overlayProps = useModalOverlayClose(onClose);
  const [name, setName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 모달 열릴 때 초기화 및 포커스
  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setError(null);
      // 약간의 딜레이 후 포커스 (모달 애니메이션 후)
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 100);
    }
  }, [isOpen, initialName]);

  if (!isOpen) return null;

  const handleSave = () => {
    const trimmed = name.trim();

    if (!trimmed) {
      setError('템플릿 이름을 입력해주세요.');
      return;
    }

    if (trimmed.length > 50) {
      setError('템플릿 이름은 50자 이내로 입력해주세요.');
      return;
    }

    onSave(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSaving) {
      handleSave();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100, // 다른 모달보다 위
      }}
      {...overlayProps}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          width: '400px',
          maxWidth: '90vw',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '20px',
          }}
        >
          <h2
            style={{
              fontSize: '16px',
              fontWeight: '600',
              color: 'var(--color-gray-800)',
              margin: 0,
            }}
          >
            템플릿 이름 입력
          </h2>
          <button
            onClick={onClose}
            disabled={isSaving}
            style={{
              background: 'none',
              border: 'none',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: isSaving ? 0.5 : 1,
            }}
          >
            <X size={18} color="var(--color-gray-500)" />
          </button>
        </div>

        {/* 입력 필드 */}
        <div style={{ marginBottom: '20px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '500',
              color: 'var(--color-gray-600)',
              marginBottom: '6px',
            }}
          >
            템플릿 이름 <span style={{ color: 'var(--color-red-500)' }}>*</span>
          </label>
          <input
            ref={inputRef}
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(null);
            }}
            onKeyDown={handleKeyDown}
            placeholder="예: 본사 3층 자리배치"
            disabled={isSaving}
            style={{
              width: '100%',
              padding: '10px 12px',
              fontSize: '14px',
              border: error
                ? '1px solid var(--color-red-500)'
                : '1px solid var(--color-gray-300)',
              borderRadius: '8px',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          {error && (
            <p
              style={{
                margin: '6px 0 0',
                fontSize: '12px',
                color: 'var(--color-red-500)',
              }}
            >
              {error}
            </p>
          )}
        </div>

        {/* 안내 문구 */}
        <p
          style={{
            margin: '0 0 20px',
            fontSize: '13px',
            color: 'var(--color-gray-500)',
            lineHeight: '1.5',
          }}
        >
          템플릿을 저장하려면 이름을 입력해야 합니다.
          <br />
          나중에 템플릿 목록에서 구분하기 쉬운 이름을 사용하세요.
        </p>

        {/* 버튼 */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onClose}
            disabled={isSaving}
            style={{
              flex: 1,
              padding: '10px 16px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '8px',
              background: 'white',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--color-gray-600)',
            }}
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !name.trim()}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '10px 16px',
              border: 'none',
              borderRadius: '8px',
              background: name.trim() ? 'var(--color-primary)' : 'var(--color-gray-300)',
              color: 'white',
              cursor: isSaving || !name.trim() ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            {isSaving ? (
              <>
                <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                저장 중...
              </>
            ) : (
              <>
                <Save size={16} />
                저장
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
